export const DELIVERY_MODE_VALUES = {
  ONLINE: "virtual",
  CLASSROOM: "in_person",
  HYBRID: "hybrid",
};

export const DELIVERY_MODES = [
  {
    value: DELIVERY_MODE_VALUES.ONLINE,
    label: "Online Batch",
    shortLabel: "Online",
    iconName: "Video",
  },
  {
    value: DELIVERY_MODE_VALUES.CLASSROOM,
    label: "Classroom Batch",
    shortLabel: "Classroom",
    iconName: "MapPin",
  },
  {
    value: DELIVERY_MODE_VALUES.HYBRID,
    label: "Hybrid Batch",
    shortLabel: "Hybrid",
    iconName: "Users",
  },
];

const cleanString = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const compactJoin = (values, separator = ", ") =>
  values.map(cleanString).filter(Boolean).join(separator);

export const normalizeDeliveryMode = (mode) =>
  Object.values(DELIVERY_MODE_VALUES).includes(mode)
    ? mode
    : DELIVERY_MODE_VALUES.ONLINE;

export const isOnlineDeliveryMode = (mode) => {
  const normalized = normalizeDeliveryMode(mode);
  return (
    normalized === DELIVERY_MODE_VALUES.ONLINE ||
    normalized === DELIVERY_MODE_VALUES.HYBRID
  );
};

export const isClassroomDeliveryMode = (mode) => {
  const normalized = normalizeDeliveryMode(mode);
  return (
    normalized === DELIVERY_MODE_VALUES.CLASSROOM ||
    normalized === DELIVERY_MODE_VALUES.HYBRID
  );
};

export const hasSessionOnlineAccess = (batchMode, session = {}) =>
  isOnlineDeliveryMode(batchMode) ||
  (normalizeDeliveryMode(batchMode) === DELIVERY_MODE_VALUES.CLASSROOM &&
    session?.remote_join_enabled === true);

export const canLearnerJoinSession = ({
  enrollmentStatus,
  batchMode,
  session = {},
  meetingLink = null,
  isInJoinWindow = false,
} = {}) =>
  ["confirmed", "completed"].includes(enrollmentStatus) &&
  hasSessionOnlineAccess(batchMode, session) &&
  Boolean(session.meeting_link || meetingLink) &&
  session.status !== "cancelled" &&
  isInJoinWindow;

export const getRemoteFallbackPresentation = (
  batchMode,
  session = {},
  hasMeetingLink = false,
  canJoinNow = false,
  isPastSession = false,
) => {
  if (
    normalizeDeliveryMode(batchMode) !== DELIVERY_MODE_VALUES.CLASSROOM ||
    session.remote_join_enabled !== true ||
    !hasMeetingLink ||
    session.status === "completed" ||
    session.status === "cancelled" ||
    isPastSession
  ) {
    return null;
  }
  return canJoinNow ? "join_now" : "available_soon";
};

export const getDeliveryModeConfig = (mode) =>
  DELIVERY_MODES.find((item) => item.value === normalizeDeliveryMode(mode)) ||
  DELIVERY_MODES[0];

export const getDeliveryModeLabel = (mode, { short = false } = {}) => {
  const config = getDeliveryModeConfig(mode);
  return short ? config.shortLabel : config.label;
};

export const normalizeClassroomDetails = (source = {}) => {
  return {
    venueName:
      cleanString(source?.venueName) ||
      cleanString(source?.classroom_venue_name),
    address:
      cleanString(source?.address) || cleanString(source?.classroom_address),
    city: cleanString(source?.city) || cleanString(source?.classroom_city),
    region:
      cleanString(source?.region) || cleanString(source?.classroom_region),
    country:
      cleanString(source?.country) || cleanString(source?.classroom_country),
    room: cleanString(source?.room) || cleanString(source?.classroom_room),
    mapUrl:
      cleanString(source?.mapUrl) || cleanString(source?.classroom_map_url),
    instructions:
      cleanString(source?.instructions) ||
      cleanString(source?.classroom_instructions),
    parkingNotes:
      cleanString(source?.parkingNotes) ||
      cleanString(source?.classroom_parking_notes),
    contactPhone:
      cleanString(source?.contactPhone) ||
      cleanString(source?.classroom_contact_phone),
  };
};

export const getClassroomLocationText = (source = {}) => {
  const details = normalizeClassroomDetails(source);
  const structuredLocation = compactJoin([
    details.venueName,
    details.room,
    details.address,
    details.city,
    details.region,
    details.country,
  ]);

  return structuredLocation || cleanString(source?.location_details) || "";
};

export const hasClassroomDetails = (source = {}) => {
  const details = normalizeClassroomDetails(source);
  return Boolean(
    getClassroomLocationText(source) ||
    details.mapUrl ||
    details.instructions ||
    details.parkingNotes ||
    details.contactPhone,
  );
};

export const getClassroomDisplayRows = (source = {}) => {
  const details = normalizeClassroomDetails(source);
  return [
    { label: "Venue", value: details.venueName },
    { label: "Room", value: details.room },
    {
      label: "Address",
      value: compactJoin([
        details.address,
        details.city,
        details.region,
        details.country,
      ]),
    },
    { label: "Arrival", value: details.instructions },
    { label: "Parking / Access", value: details.parkingNotes },
    { label: "Contact", value: details.contactPhone },
  ].filter((row) => row.value);
};

export const buildBatchDeliveryPayload = ({
  deliveryMode,
  meetingLink,
  classroomDetails,
  onlineProvider,
} = {}) => {
  const normalizedMode = normalizeDeliveryMode(deliveryMode);
  const classroomEnabled = isClassroomDeliveryMode(normalizedMode);
  const onlineEnabled = isOnlineDeliveryMode(normalizedMode);
  const details = normalizeClassroomDetails(classroomDetails);
  const classroomSource = { ...details };
  const locationText = classroomEnabled
    ? getClassroomLocationText(classroomSource)
    : "";

  return {
    delivery_mode: normalizedMode,
    meeting_link: onlineEnabled ? cleanString(meetingLink) : null,
    meeting_provider: onlineEnabled ? cleanString(onlineProvider) : null,
    location_details: classroomEnabled ? locationText || null : null,
    classroom_venue_name: classroomEnabled ? details.venueName : null,
    classroom_address: classroomEnabled ? details.address : null,
    classroom_city: classroomEnabled ? details.city : null,
    classroom_region: classroomEnabled ? details.region : null,
    classroom_country: classroomEnabled ? details.country : null,
    classroom_room: classroomEnabled ? details.room : null,
    classroom_map_url: classroomEnabled ? details.mapUrl : null,
    classroom_instructions: classroomEnabled ? details.instructions : null,
    classroom_parking_notes: classroomEnabled ? details.parkingNotes : null,
    classroom_contact_phone: classroomEnabled ? details.contactPhone : null,
  };
};

export const getCalendarDeliveryDetails = (session = {}, batch = {}) => {
  const meetingLink =
    cleanString(session.meeting_link) || cleanString(batch.meeting_link);
  const classroomLocation =
    getClassroomLocationText(session) || getClassroomLocationText(batch);
  const lines = [];

  if (hasSessionOnlineAccess(batch.delivery_mode, session) && meetingLink) {
    lines.push(`Join: ${meetingLink}`);
  }

  if (isClassroomDeliveryMode(batch.delivery_mode) && classroomLocation) {
    lines.push(`Classroom: ${classroomLocation}`);
  }

  return {
    location:
      (isClassroomDeliveryMode(batch.delivery_mode) && classroomLocation) ||
      (hasSessionOnlineAccess(batch.delivery_mode, session) && meetingLink) ||
      "",
    descriptionLines: lines,
  };
};
